import { z } from "zod";

const identifierSchema = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[a-zA-Z0-9][a-zA-Z0-9._-]*$/u);

export const ownedMcpConnectionSchema = z.object({
    connectionId: z.uuid(),
    serverId: identifierSchema,
    enabled: z.boolean(),
    status: z.enum([
        "connected",
        "disconnected",
        "needs_reauth",
        "revoked",
        "declined",
        "unavailable",
    ]),
    externalAccountId: z.string().nullable().default(null),
    externalAccountLabel: z.string().nullable(),
    grantedScopes: z.array(z.string()),
    lastConnectedAt: z.string().nullable().default(null),
    lastErrorCode: z.string().nullable(),
});

export const mcpServerIconPathSchema = z
    .string()
    .max(128)
    .regex(
        /^\/images\/integrations\/[a-z0-9][a-z0-9-]{0,63}\.(?:png|svg|webp)$/u,
    );

export const mcpServerPresentationSchema = z.object({
    serverId: identifierSchema,
    name: z.string().trim().min(1).max(160),
    iconPath: mcpServerIconPathSchema.nullable().catch(null),
});

export const listOwnedMcpConnectionsResponseSchema = z.object({
    connections: z.array(ownedMcpConnectionSchema).max(1_000),
    servers: z.array(mcpServerPresentationSchema).max(128).optional(),
});

export const mcpServerCatalogResponseSchema = z.object({
    servers: z.array(mcpServerPresentationSchema).max(128),
});

export const createMcpAuthorizationResponseSchema = z.object({
    authorizationId: identifierSchema,
    authorizationUrl: z.url({ protocol: /^https?$/u }),
    expiresAt: z.iso.datetime({ offset: true }),
});

export const mcpAuthorizationStatusResponseSchema = z.discriminatedUnion(
    "status",
    [
        z.object({
            authorizationId: identifierSchema,
            status: z.literal("pending"),
        }),
        z.object({
            authorizationId: identifierSchema,
            status: z.literal("connected"),
            connection: z.object({
                connectionId: z.uuid().optional(),
                agentId: identifierSchema,
                serverId: identifierSchema,
                externalAccountLabel: z.string().nullable(),
                grantedScopes: z.array(z.string()),
            }),
        }),
        z.object({
            authorizationId: identifierSchema,
            status: z.literal("failed"),
            errorCode: z.string().min(1).max(128),
        }),
        z.object({
            authorizationId: identifierSchema,
            status: z.literal("expired"),
        }),
    ],
);

export type OwnedMcpConnection = z.infer<typeof ownedMcpConnectionSchema>;
export type McpServerPresentation = z.infer<typeof mcpServerPresentationSchema>;
export type OwnedMcpConnectionList = z.infer<
    typeof listOwnedMcpConnectionsResponseSchema
>;
export type CreateMcpAuthorizationResponse = z.infer<
    typeof createMcpAuthorizationResponseSchema
>;
export type McpAuthorizationStatusResponse = z.infer<
    typeof mcpAuthorizationStatusResponseSchema
>;
